/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import { EventProps, TrackerConfig, TrackerEvent, UserEvent } from '../../types/services';

import { trackEvents } from 'common/config/events';
import { history } from 'common/config/routes/routes';

class SentryTracker extends TrackerEvent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private transaction: any = null;

  valid = (key: keyof TrackerConfig) => {
    return trackEvents.sentry.enabled && trackEvents.sentry[key];
  };

  /**
   * Initialize Sentry intance
   * @param init boolean
   */
  init = () => {
    if (this.valid('enabled')) {
      try {
        Sentry.init({
          dsn: process.env.REACT_APP_SENTRY_DSN,
          integrations: [
            new Integrations.BrowserTracing({
              routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
              startTransactionOnPageLoad: true,
              startTransactionOnLocationChange: true,
            }),
            new Sentry.Integrations.Breadcrumbs({
              console: true,
              dom: true,
              history: true,
              sentry: true,
              xhr: true,
            }),
          ],
          environment: process.env.REACT_APP_ENV,
          release: `${process.env.REACT_APP_SENTRY_PROJECT}@${process.env.REACT_APP_VERSION}`,
          tracesSampleRate: 0.5,
        });
        super.init();
      } catch {}
    }
  };

  /**
   * Track event on Sentry
   * @param eventName string
   * @param props { [key: string]: string | number | booolean }
   */
  trackEvent = (eventName: string, props: EventProps) => {
    this.trackSentryEvent(eventName, props);
  };

  /**
   * Internal track event
   * @param message string
   * @param tags { [key: string]: string | number | boolean}
   * @param level Sentry.Severity
   */
  trackSentryEvent = (message: string, tags?: EventProps) => {
    if (this.inicialized && this.valid('event')) {
      Sentry.addBreadcrumb({
        category: 'event',
        message,
        level: 'info' as Sentry.SeverityLevel,
        data: tags,
        type: 'transaction',
      });
    }
  };

  /**
   * Track user on Sentry
   * @param user UserEvent
   * @param newUser boolean
   */
  trackUser = (user: UserEvent) => {
    if (this.inicialized && this.valid('user')) {
      const sentryUser = user
        ? {
            id: user.id,
            email: user.email,
            username: user.username,
            country: user.country,
            language: user.language,
            id_external: user.id_external,
            status: user.status,
          }
        : null;
      Sentry.setUser(sentryUser);
      Sentry.addBreadcrumb({
        category: 'event',
        message: 'Identify User',
        level: 'info' as Sentry.SeverityLevel,
        data: !!sentryUser ? sentryUser : {},
      });
      Sentry.setTag('country', user.country || 'unknown');
      Sentry.setTag('version', process.env.REACT_APP_VERSION || '');
    }
  };

  /**
   * Track error on Sentry
   * @param error unknown
   * @param errorInfo React.ErrorInfo
   */
  trackError = (error: unknown, errorInfo: any) => {
    if (this.inicialized && this.valid('error')) {
      Sentry.setExtras({ ...(error as any), ...errorInfo });
      Sentry.captureException(error);
    }
  };
}

const SentryTrackerInstance = new SentryTracker();

export default SentryTrackerInstance;
